// Reception dashboard — API layer (RON-5469).
//
// THE SWAP POINT. Every function is `async` and resolves to the app's standard envelope
// (`{ Data: { ResponseCode, Records } }`), exactly like the real Layer A wrappers, so no screen
// changes when the endpoints are wired.
//
// The mocks return rows in the API's own snake_case shape and run them through
// `receptionApiMapper.js`, so the mapping is exercised on every read in the browser rather than
// only in its unit test.
//
// Base path is `/api/v6`. Every call is scoped to one site (§2) and only HTTP 401 is a real error
// status — the outcome lives in `Data.ResponseCode`.

import { post } from "../../api/HttpClient.js";
import {
  BASE_URL,
  ReceptionBoard_url,
  ReceptionSites_url,
} from "../../config/ApiConfig.js";
import {
  boardFromApi,
  boardQueryToApi,
  rowFromApi,
  sitesFromApi,
  walkInToApi,
} from "./receptionApiMapper.js";
import { DEFAULT_PER_PAGE, MAX_PER_PAGE, RECEPTION_FILTER } from "./receptionConstants.js";
import {
  MOCK_HOSTS,
  MOCK_RECEPTION_PIN,
  MOCK_ROWS_BY_DATE,
  MOCK_SITES,
  MOCK_TODAY,
  USE_RECEPTION_MOCKS,
} from "./receptionMockData.js";

// Enough latency that loading states are actually exercised in review instead of resolving in the
// same tick and never appearing.
const MOCK_LATENCY_MS = 280;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const mockResponse = (records) => ({
  Data: { ResponseCode: 200, Records: records },
});

const mockFailure = (code, message) => ({
  Data: { ResponseCode: code, ErrorMessage: message },
});

// 2xx, not just 200. `POST /walk-ins` answers **201** (§5) — a created resource — and every other
// write here answers 200. Reading only 200 would treat a successful walk-in as a failure.
export const isSuccessfulResponse = (response) => {
  const code = Number(response?.Data?.ResponseCode);

  return code >= 200 && code < 300;
};

// The app's shared `handleApiUiResponse` branches on `code === 200` exactly, so a 201 falls
// through to its error path and a registered walk-in would raise an error toast.
//
// Normalised HERE rather than by widening that helper: it is used by every screen in the product,
// and loosening its success test to satisfy one endpoint is a change with a blast radius far
// larger than the problem. The API layer keeps returning the contract's real code — the tests
// assert on 201 — and only the copy handed to the UI helper is rewritten.
export const toUiResponse = (response) => {
  if (Number(response?.Data?.ResponseCode) !== 201) return response;

  return { ...response, Data: { ...response.Data, ResponseCode: 200 } };
};

const notWired = (name) => {
  throw new Error(
    `Reception APIs are not wired yet (${name}). Set USE_RECEPTION_MOCKS = true in ` +
      "receptionMockData.js, or implement this function against the real endpoint."
  );
};

// ── Mock board assembly ──────────────────────────────────────────────────────────────────────
//
// The mock does the filtering, searching, counting and slicing the real endpoint does, rather
// than returning everything and letting the screen pretend. That is what makes the empty states,
// the tab counts and the paging real in design review.

// ── Session store ────────────────────────────────────────────────────────────────────────────
//
// Check-in, check-out and VIP MUST persist for the length of the session, or the board reads
// "Expected" a second after somebody was checked in and the flow looks broken in review. The
// store is a copy, so `receptionMockData.js` stays a pure fixture and a reload resets everything.
//
// It re-seeds when the fixture's identity changes, which is what lets a test swap the rows
// between cases without a reset hook — but a test must then assign a FRESH object each time, or
// one case's check-ins leak into the next.
let rowStore = null;
let seededFrom = null;

const store = () => {
  if (rowStore === null || seededFrom !== MOCK_ROWS_BY_DATE) {
    rowStore = Object.fromEntries(
      Object.entries(MOCK_ROWS_BY_DATE).map(([date, rows]) => [
        date,
        rows.map((row) => ({ ...row, compliance: { ...row.compliance } })),
      ])
    );
    seededFrom = MOCK_ROWS_BY_DATE;
  }

  return rowStore;
};

const rowsForDate = (date) => store()[date] || [];

const findRow = (id) => {
  for (const rows of Object.values(store())) {
    const found = rows.find((row) => row.id === id);
    if (found) return found;
  }

  return null;
};

const stamp = () => new Date().toISOString().slice(0, 19).replace("T", " ");

// COUNTS DESCRIBE THE WHOLE DAY AND SITE, not the filtered set (§3). Computed from every row for
// the date, before `filter` or `q` is applied — a tab badge that shrank as you typed would be
// answering a different question.
const countsForRows = (rows) => {
  const has = (predicate) => rows.filter(predicate).length;

  return {
    all: rows.length,
    expected: has((r) => r.status === "expected"),
    on_site: has((r) => r.status === "checked_in"),
    walk_ins: has((r) => r.is_walk_in),
    vip: has((r) => r.is_vip),
    contractors: has((r) => r.kind === "contractor"),
    overstay: has((r) => r.is_overstay),
  };
};

const FILTER_PREDICATES = {
  [RECEPTION_FILTER.ALL]: () => true,
  [RECEPTION_FILTER.EXPECTED]: (r) => r.status === "expected",
  [RECEPTION_FILTER.ON_SITE]: (r) => r.status === "checked_in",
  [RECEPTION_FILTER.WALK_INS]: (r) => r.is_walk_in,
  [RECEPTION_FILTER.VIP]: (r) => r.is_vip,
  [RECEPTION_FILTER.CONTRACTORS]: (r) => r.kind === "contractor",
  [RECEPTION_FILTER.OVERSTAY]: (r) => r.is_overstay,
};

// `q` searches visitor name, company, email, booking code, and a contractor's name and firm.
const matchesQuery = (row, term) =>
  [row.name, row.company, row.email, row.booking_code, row.contractor?.trade]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(term));

// ── Endpoints ────────────────────────────────────────────────────────────────────────────────

// The sites this receptionist may open a board for. Carries `reception_pin_set` so the lock
// screen knows whether to draw a PIN box at all (§7b).
export const GetReceptionSites_api = async () => {
  if (!USE_RECEPTION_MOCKS) {
    return post({ url: `${BASE_URL}${ReceptionSites_url}`, method: "GET" });
  }

  await delay(MOCK_LATENCY_MS);

  // `items`, matching the real payload's key (§2) — the fixtures are in wire format, so the
  // mapper does the same work on both paths.
  return mockResponse({ items: MOCK_SITES.map((site) => ({ ...site })), count: MOCK_SITES.length });
};

// Mapped, so callers get one shape whichever path answered.
export const fetchReceptionSites = async () => {
  const response = await GetReceptionSites_api();

  if (!isSuccessfulResponse(response)) return { ok: false, response, sites: [] };

  return { ok: true, response, sites: sitesFromApi(response.Data.Records) };
};

// `GET /visitor/reception/board` — the whole first paint (§3).
//
// One call returns the counts (tiles + tabs), the rows, the paging and `as_at`. Nothing else is
// needed to draw the screen.
export const GetReceptionBoard_api = async (requestParams = {}) => {
  // Validation runs on BOTH paths — it is what refuses a missing site or a bad filter before the
  // request leaves, and the mock must fail the same way the server does.
  let query;
  try {
    query = boardQueryToApi(requestParams);
  } catch (error) {
    // The server answers 422 for a bad filter/page/per_page. Mirrored rather than thrown, so the
    // screen's error path is the one exercised either way.
    return mockFailure(422, error.message);
  }

  // ── The real endpoint ──────────────────────────────────────────────────────────────────────
  //
  // A GET with a query string, so the params go in the URL rather than a body. `post` hands the
  // full URL to `fetchWithTokenRefresh`, which attaches the app's existing bearer token and
  // retries once on a 401 — the same auth every other screen uses, nothing new for reception.
  //
  // `BASE_URL` and `API_V6` come from the environment, so no host is pinned here.
  if (!USE_RECEPTION_MOCKS) {
    const search = new URLSearchParams(query).toString();

    return post({ url: `${BASE_URL}${ReceptionBoard_url}?${search}`, method: "GET" });
  }

  await delay(MOCK_LATENCY_MS);

  const date = query.date || MOCK_TODAY;
  const site = MOCK_SITES.find((candidate) => candidate.id === query.site_id);

  // "Not yours" is a 404 across this module, and it never volunteers that the id does not exist.
  if (!site) return mockFailure(404, "That site was not found.");

  const dayRows = rowsForDate(date);
  const counts = countsForRows(dayRows);

  const filter = query.filter || RECEPTION_FILTER.ALL;
  const term = String(query.q || "").trim().toLowerCase();

  // filter + q + date COMBINE — three narrowing conditions on one query, not three passes.
  const matched = dayRows
    .filter(FILTER_PREDICATES[filter])
    .filter((row) => !term || matchesQuery(row, term));

  const page = Number(query.page) || 1;
  const perPage = Number(query.per_page) || DEFAULT_PER_PAGE;
  const start = (page - 1) * perPage;
  const items = matched.slice(start, start + perPage);

  return mockResponse({
    // The server's clock at the read — nothing is cached.
    as_at: new Date().toISOString().slice(0, 19).replace("T", " "),
    date,
    // Returned so the TODAY pill never has to compare clocks with the server.
    is_today: date === MOCK_TODAY,
    site: { id: site.id, name: site.name },

    counts,
    filter,
    q: query.q ?? null,

    items,
    count: items.length,

    page,
    per_page: perPage,
    // The whole FILTERED set, not the page.
    total: matched.length,
    has_more: start + items.length < matched.length,
  });
};

// Convenience wrapper: one call, mapped, for the screen. Kept here rather than in the shell so
// every caller gets the same treatment and the envelope is read in exactly one place.
export const fetchReceptionBoard = async (requestParams) => {
  const response = await GetReceptionBoard_api(requestParams);

  if (!isSuccessfulResponse(response)) return { ok: false, response, board: null };

  return { ok: true, response, board: boardFromApi(response.Data.Records) };
};

// Every row matching the current filter/search/date, not just the page on screen (R5, export).
//
// "For a muster, walk the pages: `has_more` is the loop condition. Don't ask for a huge
//  `per_page` — the cap will quietly give you 200 and a fire warden would never know."
//
// So this walks, at the documented maximum. The page cap is a guard against a paging bug turning
// an export into an infinite loop at a front desk; it is deliberately far above any real day.
const MAX_EXPORT_PAGES = 50;

export const fetchAllBoardRows = async (requestParams = {}) => {
  const rows = [];
  let page = 1;

  for (let guard = 0; guard < MAX_EXPORT_PAGES; guard += 1) {
    const response = await GetReceptionBoard_api({
      ...requestParams,
      Page: page,
      PerPage: MAX_PER_PAGE,
    });

    if (!isSuccessfulResponse(response)) return { ok: false, response, rows: [] };

    const records = response.Data.Records;
    rows.push(...(records.items || []).map(rowFromApi));

    if (!records.has_more) return { ok: true, response, rows, complete: true };

    page += 1;
  }

  // Ran out of pages before the server ran out of rows. Reported rather than silently truncated —
  // an export that stops short without saying so is the same failure as a muster that stops at
  // 200.
  return { ok: true, response: null, rows, complete: false };
};

// `GET /visitor/reception/hosts` (§5) — the picker behind the walk-in dialog.
//
// NOT `GET /visitor/hosts/candidates`, which is gated on `vcm.host.view` (admin-only) and would
// 403 for a receptionist. This endpoint exists solely so the walk-in dialog works for the person
// it is for. Lists every active employee, capped at 50, with `?q=` for a type-ahead.
export const GetReceptionHosts_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("GetReceptionHosts_api");

  await delay(MOCK_LATENCY_MS);

  const term = String(requestParams.Query || "").trim().toLowerCase();
  const items = MOCK_HOSTS.filter(
    (host) => !term || `${host.name} ${host.email}`.toLowerCase().includes(term)
  ).slice(0, 50);

  return mockResponse({ items, count: items.length, truncated: false, limit: 50 });
};

// ── Writes (§6, §7, §8) ──────────────────────────────────────────────────────────────────────
//
// All of these accept an `Idempotency-Key`. §8's reasoning applies harder at a front desk than
// anywhere else — "there is a person standing there waiting" — so a double-tap must not register
// two people. A replay returns the first result with `idempotent_replay` at the TOP LEVEL of the
// envelope, not inside `Records`, so a caller can spot one without knowing the endpoint's shape.
//
// Only a 2xx is remembered: a refused first call releases the key, so a corrected retry works.
const idempotencyLog = new Map();

const withIdempotency = (key, produce) => {
  if (key && idempotencyLog.has(key)) {
    return { ...idempotencyLog.get(key), idempotent_replay: true };
  }

  const response = produce();

  // "Only a 2xx is remembered, so a refused first call releases the key and a corrected retry
  // works." 2xx, NOT `=== 200`: `POST /walk-ins` answers 201, and remembering only 200 would
  // leave the one endpoint where a double-tap invents a person entirely unprotected.
  if (key && isSuccessfulResponse(response)) {
    idempotencyLog.set(key, response);
  }

  return response;
};

// Every write is scoped to a site, like every read (§2).
const requireSite = (requestParams) =>
  requestParams.SiteID ? null : mockFailure(422, "site_id is required.");

// THE CONTRACTOR GATE (§6). Four distinct reasons, all 409, all phrased so a receptionist can
// read them out at the door:
//
//   "Show these, don't summarise them. Letting an uncleared contractor past the desk is the exact
//    outcome the whole vetting module exists to prevent, and the receptionist is the person who
//    has to explain it at the door."
const contractorCheckInRefusal = (row) => {
  const contractor = row.contractor;
  if (!contractor) return null;

  if (contractor.is_provisional) {
    return `${row.name} has not been cleared. Vetting has to approve it before they can come on site.`;
  }

  if (contractor.status === "archived") {
    return `${row.name} is archived on the contractor register and cannot be admitted.`;
  }

  // Measured against the visit, not against today: somebody cleared now can still be uncleared on
  // the day they are standing here.
  if (
    contractor.status === "expired" ||
    (contractor.vetted_until && contractor.vetted_until < row.start_date)
  ) {
    return `${row.name}'s clearance expired on ${contractor.vetted_until}. It has to be extended or re-vetted before they can come on site.`;
  }

  if (contractor.status !== "vetted") {
    return `${row.name}'s vetting is ${contractor.status}. They cannot come on site until it is approved.`;
  }

  return null;
};

// `POST /visitor/reception/visits/{id}/check-in` (§6).
export const CheckInVisit_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("CheckInVisit_api");

  const missingSite = requireSite(requestParams);
  if (missingSite) return missingSite;

  await delay(MOCK_LATENCY_MS);

  return withIdempotency(requestParams.IdempotencyKey, () => {
    const row = findRow(requestParams.VisitID);
    if (!row) return mockFailure(404, "That visit was not found.");

    // "Re-checking-in a departed visit is refused and points at registering a fresh walk-in: a
    //  returning visitor is a new visit, because the check-in events are the fire record and
    //  re-using a closed visit would blur two presences into one row."
    if (row.status === "checked_out") {
      return mockFailure(
        409,
        `${row.name} has already signed out. Register them as a new walk-in if they have returned.`
      );
    }

    if (row.status === "checked_in") {
      return mockFailure(409, `${row.name} is already checked in.`);
    }

    const refusal = contractorCheckInRefusal(row);
    if (refusal) return mockFailure(409, refusal);

    row.status = "checked_in";
    row.checked_in_at = stamp();
    row.can_check_in = false;
    row.can_check_out = true;

    return {
      Data: {
        ResponseCode: 200,
        // The host is emailed on ARRIVAL ONLY — nobody needs an email saying their visitor went
        // home. `host_notified` says whether it actually went, and a false must be surfaced: a
        // receptionist who believes the host was emailed has no reason to phone them instead.
        Message: "Checked in. Their host has been emailed.",
        Records: { status: "checked_in", at: row.checked_in_at, host_notified: true },
      },
    };
  });
};

// `POST /visitor/reception/visits/{id}/check-out` (§6).
//
// The ONLY way somebody leaves the board. There is no automatic check-out and no Wi-Fi or
// geofence presence — a deliberate design rule, not a gap.
export const CheckOutVisit_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("CheckOutVisit_api");

  const missingSite = requireSite(requestParams);
  if (missingSite) return missingSite;

  await delay(MOCK_LATENCY_MS);

  return withIdempotency(requestParams.IdempotencyKey, () => {
    const row = findRow(requestParams.VisitID);
    if (!row) return mockFailure(404, "That visit was not found.");

    if (row.status !== "checked_in") {
      return mockFailure(409, `${row.name} is not checked in.`);
    }

    row.status = "checked_out";
    row.checked_out_at = stamp();
    row.can_check_out = false;
    // Checking out clears the overstay. The flag exists to say somebody is still here past their
    // time; once they have left it is answering a question nobody is asking.
    row.is_overstay = false;
    row.overstay_by = null;

    return {
      Data: {
        ResponseCode: 200,
        Message: "Signed out.",
        // `host_notified` is null on check-out, not false — nothing was attempted.
        Records: { status: "checked_out", at: row.checked_out_at, host_notified: null },
      },
    };
  });
};

// `PATCH /visitor/reception/visits/{id}/vip` (§7).
export const SetVisitVip_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("SetVisitVip_api");

  const missingSite = requireSite(requestParams);
  if (missingSite) return missingSite;

  await delay(MOCK_LATENCY_MS);

  return withIdempotency(requestParams.IdempotencyKey, () => {
    const row = findRow(requestParams.VisitID);
    if (!row) return mockFailure(404, "That visit was not found.");

    const next = requestParams.IsVip === true;

    // "Flagging an already-flagged VIP is a 200 ... not an error. The desk and the database
    //  already agree." Same for unflagging.
    if (row.is_vip === next) {
      return {
        Data: {
          ResponseCode: 200,
          Message: next ? "Already flagged as a VIP." : "Already not a VIP.",
          Records: { is_vip: next },
        },
      };
    }

    row.is_vip = next;

    return {
      Data: {
        ResponseCode: 200,
        Message: next ? "Flagged as a VIP. Security notified." : "VIP flag removed.",
        Records: { is_vip: next },
      },
    };
  });
};

// `POST /visitor/reception/walk-ins` (§5).
//
// ONE CALL registers the person and checks them in. Answers **201**, not 200.
export const CreateWalkIn_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("CreateWalkIn_api");

  let body;
  try {
    body = walkInToApi(requestParams);
  } catch (error) {
    // The server answers 422 for a missing name or host, "a 422 that says so". Mirrored rather
    // than thrown so the screen's error path is the one exercised.
    return mockFailure(422, error.message);
  }

  // Optional, but validated when present: a typo means the host is told about somebody the system
  // can never contact again.
  if (body.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(body.email)) {
    return mockFailure(422, "That email address is not valid.");
  }

  await delay(MOCK_LATENCY_MS);

  return withIdempotency(requestParams.IdempotencyKey, () => {
    const site = MOCK_SITES.find((candidate) => candidate.id === body.site_id);
    if (!site) return mockFailure(404, "That site was not found.");

    const host = MOCK_HOSTS.find((candidate) => candidate.id === body.host_id);
    if (!host) return mockFailure(422, "That host was not found.");

    const date = requestParams.Date || MOCK_TODAY;
    const now = stamp();
    const id = `r-walkin-${Math.random().toString(36).slice(2, 8)}`;

    const row = {
      id,
      booking_code: `W-${id.slice(-6).toUpperCase()}`,
      name: body.name,
      company: body.company ?? null,
      email: body.email ?? null,
      kind: "visitor",
      is_vip: body.is_vip === true,
      is_walk_in: true,
      // Registered AND checked in, in one call.
      status: "checked_in",
      is_overstay: false,
      overstay_by: null,
      host: host.name,
      building: null,
      purpose: body.purpose ?? null,
      start_date: date,
      end_date: date,
      arrival_time: now.slice(11),
      expected_out: body.expected_out ?? null,
      checked_in_at: now,
      checked_out_at: null,
      personnel: 1,
      contractor: null,
      // NO `hs_acknowledged` / `nda_signed`, even though the dialog's switches were set. Those
      // switches record what the desk REQUIRED, not what anybody signed (A16) — and a walk-in
      // never passes through the kiosk that captures them. Writing them here would put a tick in
      // the compliance panel for something nobody was asked.
      compliance: { photo_captured: false, badge_number: null, badge_state: null },
      can_check_in: false,
      can_check_out: true,
    };

    if (!store()[date]) store()[date] = [];
    store()[date].unshift(row);

    return {
      Data: {
        // 201, not 200 — a created resource. `toUiResponse` normalises it for the shared UI
        // helper; everything else here reads it as-is.
        ResponseCode: 201,
        Message: "Walk-in registered and checked in. Their host has been emailed.",
        Records: { id, status: "checked_in", host_notified: true },
      },
    };
  });
};

// ── The lock screen (§7b) ────────────────────────────────────────────────────────────────────
//
// `POST /visitor/reception/unlock  { site_id, pin }`
//
// WHY THIS IS A SERVER CALL AT ALL:
//
//   "The browser at the desk still holds a valid Ronspot session while the screen is locked, so a
//    check done in JavaScript is a check a passer-by skips by reloading the page. The secret has
//    to live here."
//
// The prototype's lock opened for any four digits. A lock that checks nothing is worse than no
// lock, because a security review takes it on trust.
//
// The PIN is set by an ADMIN, never the receptionist — `PUT /visitor/sites/{id}/reception-pin`,
// behind `vcm.site.manage`. "A lock the person it guards can change is not one."
const UNLOCK_MAX_ATTEMPTS = 5;
const UNLOCK_LOCKOUT_SECONDS = 15 * 60;

// Per site, like the real lockout.
const unlockAttempts = new Map();

export const UnlockReception_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("UnlockReception_api");

  const missingSite = requireSite(requestParams);
  if (missingSite) return missingSite;

  await delay(MOCK_LATENCY_MS);

  const siteId = requestParams.SiteID;
  const site = MOCK_SITES.find((candidate) => candidate.id === siteId);
  if (!site) return mockFailure(404, "That site was not found.");

  const state = unlockAttempts.get(siteId) || { count: 0, until: 0 };
  const now = Date.now();

  // "The correct PIN is refused too while locked" — otherwise the lockout is only a delay for
  // somebody who already knows the answer, and no obstacle at all to somebody guessing.
  if (state.until > now) {
    return {
      Data: {
        ResponseCode: 409,
        ErrorMessage: "Too many attempts. Try again shortly.",
        Records: { locked_for_seconds: Math.ceil((state.until - now) / 1000) },
      },
    };
  }

  const pin = String(requestParams.Pin || "");

  // A site with no PIN answers 403 IDENTICALLY to a wrong PIN. "It never volunteers that there is
  // nothing to guess" — telling somebody the door is unlocked is worse than refusing them.
  const correct = site.reception_pin_set && pin === MOCK_RECEPTION_PIN;

  if (!correct) {
    const count = state.count + 1;

    if (count >= UNLOCK_MAX_ATTEMPTS) {
      unlockAttempts.set(siteId, { count: 0, until: now + UNLOCK_LOCKOUT_SECONDS * 1000 });

      return {
        Data: {
          ResponseCode: 409,
          ErrorMessage: "Too many attempts. Try again shortly.",
          Records: { locked_for_seconds: UNLOCK_LOCKOUT_SECONDS },
        },
      };
    }

    unlockAttempts.set(siteId, { count, until: 0 });

    // Deliberately says nothing about how many attempts are left: four digits is ten thousand
    // combinations, and a countdown is a hint.
    return mockFailure(403, "That PIN was not recognised.");
  }

  unlockAttempts.set(siteId, { count: 0, until: 0 });

  return mockResponse({ unlocked: true });
};

// `POST /visitor/reception/visits/{id}/notify-host` (§7).
//
// `channel` is `email` or `push` and EXACTLY ONE IS SENT — the dialog's tabs are not decoration.
// Omitted, it is `email`, so every existing call is unchanged.
export const NotifyHost_api = async (requestParams = {}) => {
  if (!USE_RECEPTION_MOCKS) notWired("NotifyHost_api");

  const missingSite = requireSite(requestParams);
  if (missingSite) return missingSite;

  await delay(MOCK_LATENCY_MS);

  return withIdempotency(requestParams.IdempotencyKey, () => {
    const row = findRow(requestParams.VisitID);
    if (!row) return mockFailure(404, "That visit was not found.");

    // "No host on the visit is a 409, not a silent no-op."
    if (!row.host) {
      return mockFailure(409, `${row.name} has no host to notify.`);
    }

    const channel = requestParams.Channel || "email";

    if (channel !== "email" && channel !== "push") {
      return mockFailure(422, 'channel must be "email" or "push".');
    }

    // "A push is a title and a line of text with nowhere for one to go, so `subject` on a `push`
    //  is a 422, not a silent drop: accepting a field and discarding it is the exact bug A15
    //  reported."
    if (channel === "push" && requestParams.Subject) {
      return mockFailure(422, "subject applies to email only; a push has nowhere to put one.");
    }

    if (requestParams.Subject && requestParams.Subject.length > 150) {
      return mockFailure(422, "subject is limited to 150 characters.");
    }

    if (requestParams.Message && requestParams.Message.length > 500) {
      return mockFailure(422, "message is limited to 500 characters.");
    }

    return {
      Data: {
        ResponseCode: 200,
        // The reply NAMES THE CHANNEL IT USED, in both the message and the records — advice
        // worded for the wrong channel sends somebody chasing the wrong cause.
        Message:
          channel === "push" ? "The host has been sent a push." : "The host has been emailed.",
        Records: { sent: true, channel },
      },
    };
  });
};
